import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, INRShort, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue, taskEffectiveAssignee, tasksForEmployee } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { TasksTable } from '../tasks/TasksPage';
import { useCounters, counterVal } from '../../core/useCounters';
import { PeriodFilter, usePeriodRange, inPeriod, DASHBOARD_PERIOD_PRESETS, useTablePeriod } from '../../components/PeriodFilter';
import { TableFilterMenu } from '../../components/TableFilterMenu';
import { SavedDashboardBar, DEFAULT_WIDGETS } from '../../components/SavedDashboardBar';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { useTableTools } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const MATRIX_PAGE = 20;

const EMP_OVERVIEW_ACCESSORS = {
  name: (r) => (r.name || '').toLowerCase(),
  clients: (r) => r.totalClients || 0,
  openClients: (r) => r.openClients || 0,
  tasks: (r) => r.totalTasks || 0,
  completed: (r) => r.completed || 0,
  pending: (r) => r.pending || 0,
  billed: (r) => r.billed || 0,
  received: (r) => r.received || 0,
  outstanding: (r) => r.outstanding || 0,
};


function AdminDashboard() {
    const { clients, invoices, tasks, employees, tickets, setView } = useApp();
    const [drill, setDrill] = useState(null);
    const [period, setPeriod] = useState("month");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [activeWidgets, setActiveWidgets] = useState(DEFAULT_WIDGETS);
    const [showTaskPanel, setShowTaskPanel] = useState(true);
    const [matrixRowsShown, setMatrixRowsShown] = useState(MATRIX_PAGE);
    const [empSearch, setEmpSearch] = useState("");
    const [empSortKey, setEmpSortKey] = useState("name");
    const [empSortDir, setEmpSortDir] = useState("asc");
    const [clientSearch, setClientSearch] = useState("");
    const [clientSortKey, setClientSortKey] = useState("name");
    const [clientSortDir, setClientSortDir] = useState("asc");
    const toggleEmpSort = (key) => {
        if (empSortKey === key) setEmpSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setEmpSortKey(key); setEmpSortDir("asc"); }
    };
    const toggleClientSort = (key) => {
        if (clientSortKey === key) setClientSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setClientSortKey(key); setClientSortDir("asc"); }
    };
    const [compactAmt, setCompactAmt] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const fn = () => setCompactAmt(mq.matches);
        fn();
        mq.addEventListener('change', fn);
        return () => mq.removeEventListener('change', fn);
    }, []);
    const fmtMoney = (n) => (compactAmt ? INRShort(n) : INR(n));
    const range = usePeriodRange(period, customFrom, customTo);
    const empPeriod = useTablePeriod("month");
    const tasksPeriod = useTablePeriod("month");
    const ticketPeriod = useTablePeriod("month");
    const clientPeriod = useTablePeriod("month");
    const [clientEmpFilter, setClientEmpFilter] = useState([]);
    const counters = useCounters();

    const completedTasks = tasks.filter(t => t.status === "completed" && inPeriod(t.completedDate, range)).length;
    const pendingTasks = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled" && inPeriod(t.dueDate || t.date, range)).length;
    const overdueTasks = tasks.filter(t => inPeriod(t.dueDate, range) && isTaskOverdue(t)).length;

    const cBilled = counters && counters !== false ? Number(counters.total_invoiced) || 0 : null;
    const cCollected = counters && counters !== false ? Number(counters.total_collected) || 0 : null;
    const STATS = [
        { id: "clients", icon: IconProperties.clients, label: "Active Clients", val: counterVal(counters, 'active_clients'), note: counterVal(counters, 'total_clients', (n) => `${n} total`), color: "var(--blue)" },
        { id: "billing", icon: IconProperties.billingSummary, label: "Total Billing", val: counterVal(counters, 'total_invoiced', fmtMoney), note: range.label, color: "var(--navy)" },
        { id: "collected", icon: IconProperties.checkCircle, label: "Collected", val: counterVal(counters, 'total_collected', fmtMoney), note: cBilled == null ? '—' : `${Math.round(cCollected / Math.max(cBilled, 1) * 100)}% · ${range.label}`, color: "var(--green)" },
        { id: "pending", icon: IconProperties.spreadsheet, label: "Outstanding", val: counterVal(counters, 'pending_collection', fmtMoney), note: range.label, color: "var(--red)" },
        { id: "pending_tasks", icon: IconProperties.tasks, label: "Pending Tasks", val: counterVal(counters, 'pending_tasks'), note: range.label, color: "var(--orange)" },
        { id: "overdue_tasks", icon: IconProperties.bell, label: "Overdue Tasks", val: counterVal(counters, 'overdue_tasks'), note: range.label, color: "var(--red)" },
        { id: "completed_tasks", icon: IconProperties.checkCircle, label: "Completed Tasks", val: counterVal(counters, 'completed_tasks'), note: range.label, color: "var(--green)" },
        { id: "employees", icon: IconProperties.employees, label: "Team Members", val: employees.length, note: "Active team", color: "var(--navy)" },
    ].filter(s => activeWidgets.includes(s.id));

    const ticketSummary = employees.map(emp => {
        const empTickets = (tickets || []).filter(t => t.assignedTo === emp.id && inPeriod(t.createdAt, ticketPeriod.range));
        return {
            emp,
            total: empTickets.length,
            open: empTickets.filter(t => t.status === "open").length,
            inProgress: empTickets.filter(t => t.status === "in_progress").length,
            resolved: empTickets.filter(t => !["open", "in_progress"].includes(t.status)).length,
        };
    }).filter(r => r.total > 0);

    const show = (id) => activeWidgets.includes(id);

    // Active workload matrix — rows/columns from live tasks + team (not period-filtered).
    const tasksByEmployeeMatrix = useMemo(() => {
        const cols = employees.filter(e => ["employee", "manager", "admin", "sales"].includes(e.role));
        const clientById = new Map(clients.map(c => [c.id, c]));
        const assigneeOf = (t) => taskEffectiveAssignee(t, clientById);
        const active = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled" && inPeriod(t.dueDate || t.date, tasksPeriod.range));
        const titles = [...new Set(active.map(t => t.title).filter(Boolean))]
            .map(title => ({
                title,
                total: active.filter(t => t.title === title).length,
            }))
            .sort((a, b) => b.total - a.total || a.title.localeCompare(b.title))
            .map(x => x.title);
        const count = (title, empId) =>
            active.filter(t => t.title === title && assigneeOf(t) === empId).length;
        const unassigned = (title) =>
            active.filter(t => {
                if (t.title !== title) return false;
                const aid = assigneeOf(t);
                return !aid || !cols.some(e => e.id === aid);
            }).length;
        const hasUnassigned = titles.some(title => unassigned(title) > 0);
        return { cols, titles, count, unassigned, hasUnassigned, activeTotal: active.length };
    }, [employees, tasks, clients, tasksPeriod.range]);

    useEffect(() => { setMatrixRowsShown(MATRIX_PAGE); }, [tasksByEmployeeMatrix.titles.length]);

    const visibleMatrixTitles = tasksByEmployeeMatrix.titles.slice(0, matrixRowsShown);
    const matrixHasMore = matrixRowsShown < tasksByEmployeeMatrix.titles.length;

    const empOverviewRows = useMemo(() => employees.map(emp => {
        const empClients = clients.filter(c => c.assignedTo === emp.id);
        const openClients = empClients.filter(c => c.status === "active").length;
        const empTasksAll = tasksForEmployee(emp.id, tasks, clients);
        const empTasks = empTasksAll.filter(t => inPeriod(t.dueDate || t.date || t.completedDate, empPeriod.range));
        const completed = empTasks.filter(t => t.status === "completed").length;
        const pending = empTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length;
        const empInvoices = invoices.filter(i => empClients.some(c => c.id === i.clientId) && i.status !== "void" && inPeriod(i.date, empPeriod.range));
        const billed = empInvoices.reduce((s, i) => s + (i.total || 0), 0);
        const received = empInvoices.reduce((s, i) => s + (i.paid || 0), 0);
        const outstanding = empInvoices.reduce((s, i) => s + (i.pending || 0), 0);
        return {
            id: emp.id, name: emp.name, totalClients: empClients.length, openClients,
            totalTasks: empTasks.length, completed, pending, billed, received, outstanding,
        };
    }), [employees, clients, tasks, invoices, empPeriod.range]);

    const empTable = useTableTools(empOverviewRows, {
        search: empSearch,
        searchFn: (r, q) => (r.name || '').toLowerCase().includes(q.toLowerCase()),
        accessors: EMP_OVERVIEW_ACCESSORS,
        sortKey: empSortKey,
        sortDir: empSortDir,
        onSort: toggleEmpSort,
    });

    const clientOverviewRows = useMemo(() => {
        let list = clients;
        if (clientEmpFilter.length) list = list.filter(c => clientEmpFilter.includes(c.assignedTo));
        return list.map(c => {
            const emp = employees.find(e => e.id === c.assignedTo);
            const clientTasks = tasks.filter(t => t.clientId === c.id && inPeriod(t.dueDate || t.date || t.completedDate, clientPeriod.range));
            const clientInvoices = invoices.filter(i => i.clientId === c.id && i.status !== "void" && inPeriod(i.date, clientPeriod.range));
            const totalBilling = clientInvoices.reduce((s, i) => s + (i.total || 0), 0);
            const collected = clientInvoices.reduce((s, i) => s + (i.paid || 0), 0);
            const pending = clientInvoices.reduce((s, i) => s + (i.pending || 0), 0);
            const done = clientTasks.filter(t => t.status === "completed").length;
            const pct = clientTasks.length ? Math.round(done / clientTasks.length * 100) : c.progress;
            return { ...c, emp, clientTasks, done, pct, totalBilling, collected, pending };
        });
    }, [clients, employees, tasks, invoices, clientPeriod.range, clientEmpFilter]);

    const CLIENT_OVERVIEW_ACCESSORS = {
        name: (r) => (r.name || '').toLowerCase(),
        createdAt: (r) => (r.createdAt ? new Date(r.createdAt).getTime() : 0),
        totalBilling: (r) => Number(r.totalBilling) || 0,
        collected: (r) => Number(r.collected) || 0,
        pending: (r) => Number(r.pending) || 0,
        progress: (r) => Number(r.pct) || 0,
    };
    const clientTable = useTableTools(clientOverviewRows, {
        search: clientSearch,
        searchFn: (r, q) => (r.name || '').toLowerCase().includes(q.toLowerCase()) || (r.clientNo || '').includes(q),
        accessors: CLIENT_OVERVIEW_ACCESSORS,
        sortKey: clientSortKey,
        sortDir: clientSortDir,
        onSort: toggleClientSort,
    });

    const ticketTable = useTableTools(ticketSummary, {
        accessors: {
            name: (r) => (r.emp?.name || '').toLowerCase(),
            total: (r) => r.total || 0,
            open: (r) => r.open || 0,
            inProgress: (r) => r.inProgress || 0,
            resolved: (r) => r.resolved || 0,
        },
        defaultSort: { key: 'total', dir: 'desc' },
    });

    return (
        <>
            <SavedDashboardBar
              period={period} customFrom={customFrom} customTo={customTo}
              setPeriod={setPeriod} setCustomFrom={setCustomFrom} setCustomTo={setCustomTo}
              activeWidgets={activeWidgets} setActiveWidgets={setActiveWidgets}
              showTaskPanel={showTaskPanel} setShowTaskPanel={setShowTaskPanel}
            />
            <PeriodFilter
              presets={DASHBOARD_PERIOD_PRESETS}
              period={period} setPeriod={setPeriod}
              customFrom={customFrom} setCustomFrom={setCustomFrom}
              customTo={customTo} setCustomTo={setCustomTo}
            />
            <div className="stat-cards dashboard-stat-cards" style={{ marginBottom: 22 }}>
                {STATS.map(s => {
                    const IconComp = s.icon;
                    return (
                        <div key={s.id} className="stat-box dashboard-stat-card" onClick={() => setDrill({ type: s.id, title: s.label })}>
                            <div className="stat-icon dashboard-stat-icon" style={{ color: s.color, background: `${s.color}15` }}>
                                {IconComp && <IconComp size={18} strokeWidth={2} />}
                            </div>
                            <div className="stat-body">
                                <div className="stat-lbl">{s.label}</div>
                                <div className="stat-val stat-val-money" style={{ color: s.color }} title={typeof s.val === 'string' && s.val.includes('₹') ? s.val : undefined}>{s.val}</div>
                                <div className="stat-note">{s.note}</div>
                                <div className="stat-drill">Tap for details →</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {showTaskPanel && show('team_panel') && (
            <div className="grid2" style={{ marginBottom: 22 }}>
                <div className="card">
                    <div className="card-head">
                        <div className="card-title">Recent Pending Tasks</div>
                        <button className="btn btn-sm" onClick={() => setView("tasks")}>View All →</button>
                    </div>
                    <div style={{ padding: "12px 16px", display: "flex", gap: 10, borderBottom: "1px solid var(--border)" }}>
                        {[["Pending", pendingTasks, "var(--orange)", "#FFF8EC"], ["Completed", completedTasks, "var(--green)", "#F0FDF4"], ["Overdue", overdueTasks, "var(--red)", "#FEF2F2"]].map(([l, n, c, bg]) => (
                            <div key={l} style={{ flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 9, background: bg, cursor: "pointer" }} onClick={() => setDrill({ type: l.toLowerCase() + "_tasks", title: l + " Tasks" })}>
                                <div style={{ fontSize: 20, fontWeight: 700, color: c }}>{n}</div>
                                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{l}</div>
                            </div>
                        ))}
                    </div>
                    {tasks.filter(t => t.status !== "completed" && t.status !== "cancelled" && inPeriod(t.dueDate || t.date, range)).slice(0, 4).map(t => (
                        <div key={t.id} className="row-item">
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</div>
                                <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.clientName}</div>
                            </div>
                            <Badge status={t.status} />
                        </div>
                    ))}
                </div>

                <div className="card">
                    <div className="card-head">
                        <div className="card-title">Team Performance</div>
                        <button className="btn btn-sm" onClick={() => setView("employees")}>View All →</button>
                    </div>
                    {employees.map(emp => {
                        const empTasksAll = tasksForEmployee(emp.id, tasks, clients);
                        const empTasks = empTasksAll.filter(t => inPeriod(t.dueDate || t.date || t.completedDate, range));
                        const empCompleted = empTasks.filter(t => t.status === "completed").length;
                        const empClients = clients.filter(c => c.assignedTo === emp.id).length;
                        const pct = empTasks.length ? Math.round(empCompleted / empTasks.length * 100) : 0;
                        return (
                            <div key={emp.id} className="row-item" style={{ cursor: "pointer" }} onClick={() => setDrill({ type: "emp_" + emp.id, title: emp.name + "'s Tasks" })}>
                                <div className="av av-md" style={{ background: emp.color }}>{emp.avatar}</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{emp.name}</div>
                                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{empClients} clients · {empCompleted}/{empTasks.length} tasks</div>
                                    <div className="prog-bg" style={{ marginTop: 5, maxWidth: 160 }}>
                                        <div className="prog-fill" style={{ width: pct + "%", background: pct === 100 ? "var(--green)" : pct > 60 ? "var(--gold)" : "var(--blue)" }} />
                                    </div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: pct === 100 ? "var(--green)" : pct > 60 ? "var(--gold-dk)" : "var(--blue)" }}>{pct}%</div>
                            </div>
                        );
                    })}
                </div>
            </div>
            )}

            {show('emp_overview') && (
            <div className="card" style={{ marginBottom: 22 }}>
                <div className="card-head">
                    <div className="card-title">Employee Overview</div>
                    <TableFilterMenu periodState={empPeriod} />
                </div>
                <FilterBar search={empSearch} setSearch={setEmpSearch} searchPlaceholder="Search employee…" onClear={() => setEmpSearch("")} />
                <div className="tbl-wrap">
                    <table>
                        <thead>
                            <tr>
                                <SortableTh label="Employee" sortKey="name" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Total Clients" sortKey="clients" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Open Clients" sortKey="openClients" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Total Tasks" sortKey="tasks" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Completed" sortKey="completed" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Pending" sortKey="pending" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Total Billing" sortKey="billed" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Received" sortKey="received" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                                <SortableTh label="Outstanding" sortKey="outstanding" activeKey={empSortKey} sortDir={empSortDir} onSort={toggleEmpSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {empTable.visible.map(row => (
                                    <tr key={row.id}>
                                        <td style={{ fontWeight: 600 }}>{row.name}</td>
                                        <td>{row.totalClients}</td>
                                        <td>{row.openClients}</td>
                                        <td>{row.totalTasks}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{row.completed}</td>
                                        <td style={{ color: "var(--orange)", fontWeight: 600 }}>{row.pending}</td>
                                        <td style={{ fontWeight: 700 }}>{fmtMoney(row.billed)}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{fmtMoney(row.received)}</td>
                                        <td style={{ color: row.outstanding > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{fmtMoney(row.outstanding)}</td>
                                    </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <LoadMoreBar hasMore={empTable.hasMore} loadingMore={false} onLoadMore={empTable.loadMore} count={empTable.shownCount} />
            </div>
            )}

            {show('tasks_by_employee') && tasksByEmployeeMatrix.cols.length > 0 && (
                <div className="card" style={{ marginBottom: 22 }}>
                    <div className="card-head">
                        <div className="card-title">Tasks by Employee</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 11, color: "var(--muted)" }}>
                                {tasksByEmployeeMatrix.activeTotal} open tasks · {tasksByEmployeeMatrix.cols.length} team members
                                {tasksByEmployeeMatrix.titles.length > MATRIX_PAGE && (
                                    <> · {visibleMatrixTitles.length}/{tasksByEmployeeMatrix.titles.length} task types</>
                                )}
                            </span>
                            <TableFilterMenu periodState={tasksPeriod} />
                        </div>
                    </div>
                    {tasksByEmployeeMatrix.titles.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                            No open tasks to show. Completed tasks are excluded.
                        </div>
                    ) : (
                        <div className="tbl-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Task</th>
                                        {tasksByEmployeeMatrix.cols.map(emp => (
                                            <th key={emp.id} style={{ fontSize: 11 }} title={emp.name}>
                                                {emp.name.split(" ")[0]}
                                            </th>
                                        ))}
                                        {tasksByEmployeeMatrix.hasUnassigned && (
                                            <th style={{ fontSize: 11, color: "var(--muted)" }}>Unassigned</th>
                                        )}
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleMatrixTitles.map(title => {
                                        const rowCounts = tasksByEmployeeMatrix.cols.map(emp =>
                                            tasksByEmployeeMatrix.count(title, emp.id),
                                        );
                                        const unassigned = tasksByEmployeeMatrix.unassigned(title);
                                        const total = rowCounts.reduce((s, n) => s + n, 0) + unassigned;
                                        return (
                                            <tr key={title}>
                                                <td style={{ fontWeight: 500, fontSize: 12 }}>{title}</td>
                                                {rowCounts.map((n, i) => (
                                                    <td key={tasksByEmployeeMatrix.cols[i].id} style={{ textAlign: "center", fontWeight: n ? 600 : 400, color: n ? "var(--navy)" : "var(--muted)" }}>
                                                        {n || "—"}
                                                    </td>
                                                ))}
                                                {tasksByEmployeeMatrix.hasUnassigned && (
                                                    <td style={{ textAlign: "center", color: unassigned ? "var(--orange)" : "var(--muted)", fontWeight: unassigned ? 600 : 400 }}>
                                                        {unassigned || "—"}
                                                    </td>
                                                )}
                                                <td style={{ fontWeight: 700, textAlign: "center" }}>{total}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <LoadMoreBar
                                hasMore={matrixHasMore}
                                loadingMore={false}
                                onLoadMore={() => setMatrixRowsShown(n => n + MATRIX_PAGE)}
                                count={visibleMatrixTitles.length}
                            />
                        </div>
                    )}
                </div>
            )}

            {show('ticket_summary') && ticketSummary.length > 0 && (
                <div className="card" style={{ marginBottom: 22 }}>
                    <div className="card-head">
                        <div className="card-title">Tickets by Employee</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button className="btn btn-sm" onClick={() => setView("tickets")}>View All →</button>
                            <TableFilterMenu periodState={ticketPeriod} />
                        </div>
                    </div>
                    <div className="tbl-wrap">
                        <table>
                            <thead>
                                <tr><th>Employee</th><th>Total</th><th>Open</th><th>In Progress</th><th>Resolved</th></tr>
                            </thead>
                            <tbody>
                                {ticketTable.visible.map(({ emp, total, open, inProgress, resolved }) => (
                                    <tr key={emp.id} style={{ cursor: "pointer" }} onClick={() => setDrill({ type: "tickets_emp_" + emp.id, title: emp.name + " — Tickets" })}>
                                        <td style={{ fontWeight: 600 }}>{emp.name}</td>
                                        <td>{total}</td>
                                        <td style={{ color: "var(--orange)", fontWeight: 600 }}>{open}</td>
                                        <td style={{ color: "var(--blue)", fontWeight: 600 }}>{inProgress}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{resolved}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <LoadMoreBar hasMore={ticketTable.hasMore} loadingMore={false} onLoadMore={ticketTable.loadMore} count={ticketTable.shownCount} />
                </div>
            )}

            {show('client_overview') && (
            <div className="card">
                <div className="card-head">
                    <div className="card-title">Client Overview</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button className="btn btn-sm" onClick={() => setView("clients")}>View All →</button>
                        <TableFilterMenu
                            periodState={clientPeriod}
                            showEmployees
                            employees={employees}
                            selectedEmpIds={clientEmpFilter}
                            setSelectedEmpIds={setClientEmpFilter}
                        />
                    </div>
                </div>
                <FilterBar
                    search={clientSearch}
                    setSearch={setClientSearch}
                    searchPlaceholder="Search client…"
                    onClear={() => { setClientSearch(""); setClientEmpFilter([]); }}
                    hasActive={!!clientSearch || clientEmpFilter.length > 0}
                />
                <div className="tbl-wrap">
                    <table>
                        <thead><tr>
                            <SortableTh label="Client" sortKey="name" activeKey={clientSortKey} sortDir={clientSortDir} onSort={toggleClientSort} />
                            <SortableTh label="Onboarded" sortKey="createdAt" activeKey={clientSortKey} sortDir={clientSortDir} onSort={toggleClientSort} />
                            <th>Assigned To</th>
                            <SortableTh label="Billing" sortKey="totalBilling" activeKey={clientSortKey} sortDir={clientSortDir} onSort={toggleClientSort} />
                            <SortableTh label="Collected" sortKey="collected" activeKey={clientSortKey} sortDir={clientSortDir} onSort={toggleClientSort} />
                            <SortableTh label="Outstanding" sortKey="pending" activeKey={clientSortKey} sortDir={clientSortDir} onSort={toggleClientSort} />
                            <th>Tasks</th>
                            <SortableTh label="Progress" sortKey="progress" activeKey={clientSortKey} sortDir={clientSortDir} onSort={toggleClientSort} />
                        </tr></thead>
                        <tbody>
                            {clientTable.visible.map(c => (
                                    <tr key={c.id}>
                                        <td><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></td>
                                        <td style={{ fontSize: 12, color: "var(--muted)" }}>{c.createdAt}</td>
                                        <td>{c.emp && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div className="av av-sm" style={{ background: c.emp.color }}>{c.emp.avatar}</div><span style={{ fontSize: 12 }}>{c.emp.name}</span></div>}</td>
                                        <td style={{ fontWeight: 700 }}>{INR(c.totalBilling)}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(c.collected)}</td>
                                        <td style={{ color: c.pending > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{INR(c.pending)}</td>
                                        <td style={{ fontSize: 12 }}>{c.done}/{c.clientTasks.length}</td>
                                        <td style={{ minWidth: 110 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <div className="prog-bg" style={{ flex: 1 }}><div className="prog-fill" style={{ width: c.pct + "%", background: c.pct === 100 ? "var(--green)" : c.pct > 60 ? "var(--gold)" : "var(--blue)" }} /></div>
                                                <span style={{ fontSize: 12, fontWeight: 700 }}>{c.pct}%</span>
                                            </div>
                                        </td>
                                    </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <LoadMoreBar hasMore={clientTable.hasMore} loadingMore={false} onLoadMore={clientTable.loadMore} count={clientTable.shownCount} />
            </div>
            )}

            {drill && <DrillPanel type={drill.type} title={drill.title} range={range} empId={drill.empId} overdueBucket={drill.overdueBucket} onClose={() => setDrill(null)} />}
        </>
    );
}

function DrillPanel({ type, title, onClose, range = null, empId = null, overdueBucket = null }) {
    const { clients, tasks, employees, invoices, payments, tickets } = useApp();
    const inRange = (dateStr) => !range ? true : inPeriod(dateStr, range);

    const matchOverdueBucket = (t) => {
        if (!overdueBucket || !t.dueDate) return true;
        const h = (new Date() - new Date(t.dueDate)) / (1000 * 3600);
        if (overdueBucket === "12-24") return h >= 12 && h < 24;
        if (overdueBucket === "24-48") return h >= 24 && h < 48;
        if (overdueBucket === "48-72") return h >= 48 && h < 72;
        if (overdueBucket === "72+") return h >= 72;
        return true;
    };

    const renderContent = () => {
        // Active clients list
        if (type === "clients") {
            const list = clients.filter(c => c.status === "active");
            if (!list.length) return <div className="empty"><div className="empty-icon"><Icon name="clients" size={28} /></div><div style={{ fontWeight: 600, marginBottom: 4 }}>No active clients yet</div><div style={{ fontSize: 12, color: "var(--muted)" }}>Active clients will appear here.</div></div>;
            return (
                <div className="tbl-wrap">
                    <table>
                        <thead><tr><th>Client</th><th>Onboarded</th><th>Type</th><th>Assigned To</th><th>Total Billing</th><th>Collected</th><th>Outstanding</th><th>Total Tasks</th><th>Pending</th><th>Completed</th></tr></thead>
                        <tbody>
                            {list.map(c => {
                                const emp = employees.find(e => e.id === c.assignedTo);
                                const clientTasks = tasks.filter(t => t.clientId === c.id);
                                const done = clientTasks.filter(t => t.status === "completed").length;
                                const pending = clientTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length;
                                const cInv = invoices.filter(i => i.clientId === c.id);
                                const billed = cInv.reduce((s, i) => s + (i.total || 0), 0), coll = cInv.reduce((s, i) => s + (i.paid || 0), 0), outst = cInv.reduce((s, i) => s + (i.pending || 0), 0);
                                return (
                                    <tr key={c.id}>
                                        <td><div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></td>
                                        <td style={{ fontSize: 12, color: "var(--muted)" }}>{c.createdAt}</td>
                                        <td><span className="tag">{c.type}</span></td>
                                        <td style={{ fontSize: 12 }}>{emp?.name || "—"}</td>
                                        <td style={{ fontWeight: 700 }}>{INR(billed)}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(coll)}</td>
                                        <td style={{ color: outst > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{INR(outst)}</td>
                                        <td>{clientTasks.length}</td>
                                        <td style={{ color: "var(--orange)", fontWeight: 600 }}>{pending}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{done}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Billing breakdown
        if (type === "billing") {
            const totalBilling = invoices.reduce((s, i) => s + (i.total || 0), 0);
            const govtFees = invoices.reduce((s, i) => s + (i.lineItems || []).filter(l => l.type === "govt").reduce((ss, l) => ss + l.amount, 0), 0);
            const gstAmt = invoices.reduce((s, i) => s + (i.total || 0) - (i.lineItems || []).reduce((ss, l) => ss + l.unitPrice * l.qty, 0), 0);
            const dscAmt = invoices.reduce((s, i) => s + (i.lineItems || []).filter(l => l.type === "dsc").reduce((ss, l) => ss + l.amount, 0), 0);
            const grossMargin = totalBilling - govtFees - gstAmt;
            return (
                <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                        {[["Total Billing", INR(totalBilling), "var(--navy)"], ["Govt Fees", INR(govtFees), "var(--muted)"], ["GST Collected", INR(gstAmt), "var(--orange)"], ["DSC Tokens", INR(dscAmt), "var(--blue)"], ["Gross Margin", INR(grossMargin), "var(--green)"], ["Collection %", Math.round((invoices.reduce((s, i) => s + (i.paid || 0), 0) / Math.max(totalBilling, 1)) * 100) + "%", "var(--gold-dk)"]].map(([l, v, c]) => (
                            <div key={l} style={{ padding: "14px 16px", background: "var(--cream)", borderRadius: 10, border: "1px solid var(--border)" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px" }}>{l}</div>
                                <div style={{ fontSize: 22, fontWeight: 700, color: c, fontFamily: "'Outfit',sans-serif", marginTop: 4 }}>{v}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)", padding: "10px 14px", background: "var(--gold-lt)", borderRadius: 8, border: "1px solid rgba(201,161,74,.3)" }}>
                        <InlineIcon name="sparkles" size={14} gap={5}>Gross Margin = Total Billing − Govt Fees − GST. This is your firm's service revenue before operational expenses.</InlineIcon>
                    </div>
                </div>
            );
        }

        // Outstanding clients — #residual derive from invoices (was reading stale c.pending=0)
        if (type === "pending") {
            const rows = clients.map(c => {
                const cInv = invoices.filter(i => i.clientId === c.id);
                return {
                    c,
                    billed: cInv.reduce((s, i) => s + (i.total || 0), 0),
                    collected: cInv.reduce((s, i) => s + (i.paid || 0), 0),
                    outstanding: cInv.reduce((s, i) => s + (i.pending || 0), 0)
                };
            }).filter(r => r.outstanding > 0).sort((a, b) => b.outstanding - a.outstanding);
            if (!rows.length) return <div className="empty"><div className="empty-icon"><Icon name="checkCircle2" size={28} /></div><div style={{ fontWeight: 600, marginBottom: 4 }}>Nothing outstanding</div><div style={{ fontSize: 12, color: "var(--muted)" }}>All invoices are fully collected.</div></div>;
            return (
                <div className="tbl-wrap">
                    <table>
                        <thead><tr><th>Client</th><th>Assigned To</th><th>Total Billed</th><th>Collected</th><th>Outstanding</th><th>Onboarded</th></tr></thead>
                        <tbody>
                            {rows.map(({ c, billed, collected, outstanding }) => {
                                const emp = employees.find(e => e.id === c.assignedTo);
                                return (
                                    <tr key={c.id}>
                                        <td><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></td>
                                        <td style={{ fontSize: 12 }}>{emp?.name || "—"}</td>
                                        <td style={{ fontWeight: 700 }}>{INR(billed)}</td>
                                        <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(collected)}</td>
                                        <td style={{ color: "var(--red)", fontWeight: 700, fontSize: 14 }}>{INR(outstanding)}</td>
                                        <td style={{ fontSize: 12, color: "var(--muted)" }}>{c.createdAt}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Collected breakdown by client
        if (type === "collected") {
            const rows = clients.map(c => {
                const cInv = invoices.filter(i => i.clientId === c.id);
                return {
                    c,
                    billed: cInv.reduce((s, i) => s + (i.total || 0), 0),
                    collected: cInv.reduce((s, i) => s + (i.paid || 0), 0),
                };
            }).filter(r => r.collected > 0).sort((a, b) => b.collected - a.collected);
            if (!rows.length) return <div className="empty"><div className="empty-icon"><Icon name="checkCircle2" size={28} /></div><div style={{ fontWeight: 600, marginBottom: 4 }}>No collections yet</div></div>;
            return (
                <div className="tbl-wrap">
                    <table>
                        <thead><tr><th>Client</th><th>Total Billed</th><th>Collected</th><th>Collection %</th></tr></thead>
                        <tbody>
                            {rows.map(({ c, billed, collected }) => (
                                <tr key={c.id}>
                                    <td><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></td>
                                    <td style={{ fontWeight: 700 }}>{INR(billed)}</td>
                                    <td style={{ color: "var(--green)", fontWeight: 700 }}>{INR(collected)}</td>
                                    <td>{billed ? Math.round(collected / billed * 100) : 0}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Team members list
        if (type === "employees") {
            if (!employees.length) return <div className="empty"><div>No team members</div></div>;
            return (
                <div className="tbl-wrap">
                    <table>
                        <thead><tr><th>Name</th><th>Role</th><th>Email</th><th>Phone</th><th>Clients</th><th>Tasks</th></tr></thead>
                        <tbody>
                            {employees.map(emp => (
                                <tr key={emp.id}>
                                    <td style={{ fontWeight: 600 }}>{emp.name}</td>
                                    <td><span className="tag">{emp.role}</span></td>
                                    <td style={{ fontSize: 12 }}>{emp.email}</td>
                                    <td style={{ fontSize: 12 }}>{emp.phone}</td>
                                    <td>{clients.filter(c => c.assignedTo === emp.id).length}</td>
                                    <td>{tasksForEmployee(emp.id, tasks, clients).length}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Pending tasks
        if (type === "pending_tasks") {
            const list = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled" && (!empId || t.assignedTo === empId) && inRange(t.dueDate || t.date));
            return <TasksTable tasks={list} showClient={true} />;
        }

        // Overdue tasks
        if (type === "overdue_tasks") {
            const list = tasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed" && (!empId || t.assignedTo === empId) && inRange(t.dueDate) && matchOverdueBucket(t));
            return <TasksTable tasks={list} showClient={true} />;
        }

        // Completed tasks
        if (type === "completed_tasks") {
            const list = tasks.filter(t => t.status === "completed" && (!empId || t.assignedTo === empId) && inRange(t.completedDate || t.dueDate || t.date));
            return <TasksTable tasks={list} showClient={true} />;
        }

        // Overdue 72hr+ (employee analytics)
        if (type === "emp_overdue_72") {
            const now = new Date();
            const list = tasks.filter(t => {
                if (!t.dueDate || t.status === "completed") return false;
                if (empId && t.assignedTo !== empId) return false;
                const h = (now - new Date(t.dueDate)) / (1000 * 3600);
                return h >= 72;
            });
            return <TasksTable tasks={list} showClient={true} />;
        }
        if (type.startsWith("tickets_emp_")) {
            const tid = type.replace("tickets_emp_", "");
            const list = (tickets || []).filter(t => t.assignedTo === tid);
            if (!list.length) return <div className="empty"><div>No tickets assigned</div></div>;
            return (
                <div className="tbl-wrap">
                    <table>
                        <thead><tr><th>Ticket</th><th>Client</th><th>Status</th><th>Priority</th><th>Opened</th></tr></thead>
                        <tbody>
                            {list.map(t => (
                                <tr key={t.id}>
                                    <td style={{ fontWeight: 600, fontSize: 13 }}>{t.subject}</td>
                                    <td style={{ fontSize: 12 }}>{t.clientName}</td>
                                    <td><Badge status={t.status} /></td>
                                    <td style={{ fontSize: 12, textTransform: "capitalize" }}>{t.priority}</td>
                                    <td style={{ fontSize: 12, color: "var(--muted)" }}>{t.createdAt}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        // Employee detail
        if (type.startsWith("emp_")) {
            const empId = type.replace("emp_", "");
            const emp = employees.find(e => e.id === empId);
            const empTasks = tasksForEmployee(empId, tasks, clients);
            const empClients = clients.filter(c => c.assignedTo === empId);
            if (!emp) return <div className="empty"><div>Employee not found</div></div>;
            return (
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0", borderBottom: "1px solid var(--border)", marginBottom: 16 }}>
                        <div className="av av-lg" style={{ background: emp.color }}>{emp.avatar}</div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>{emp.name}</div>
                            <div style={{ fontSize: 12, color: "var(--muted)" }}>{emp.email} · {emp.phone}</div>
                        </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
                        {[["Clients", empClients.length, "var(--blue)"], ["Completed", empTasks.filter(t => t.status === "completed").length, "var(--green)"], ["Pending", empTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length, "var(--orange)"]].map(([l, v, c]) => (
                            <div key={l} style={{ padding: "12px", background: "var(--cream)", borderRadius: 9, textAlign: "center", border: "1px solid var(--border)" }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: c }}>{v}</div>
                                <div style={{ fontSize: 11, color: "var(--muted)" }}>{l}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "var(--navy)" }}>Assigned Clients</div>
                    {empClients.map(c => (
                        <div key={c.id} className="row-item">
                            <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: c.pending > 0 ? "var(--red)" : "var(--green)" }}>{INR(c.pending)} outstanding</div>
                        </div>
                    ))}
                    <div style={{ fontSize: 13, fontWeight: 700, margin: "16px 0 10px", color: "var(--navy)" }}>All Tasks</div>
                    <TasksTable tasks={empTasks} showClient={true} />
                </div>
            );
        }

        return <div className="empty"><div>No data available</div></div>;
    };

    return (
        <div className="drill-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="drill-panel">
                <div className="drill-head">
                    <div className="drill-title">{title}</div>
                    <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--muted)", lineHeight: 1 }}><Icon name="close" size={20} /></button>
                </div>
                <div className="drill-body">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}



export { AdminDashboard, DrillPanel };